/**
 * PocketpillsHome — pocketpills.com-style top-of-page block for
 * Central Rentals. Keeps the brand's emerald palette (no purple) but
 * borrows the pattern language: floating pill header, rounded pastel
 * hero card, chip trust row, portrait-in-blob eligibility card, pill
 * pricing cards, checklist card, dark rounded CTA hero, quick-start
 * icon grid, and a coverage list card.
 *
 * Drops directly into Landing.jsx replacing the legacy hero + trust
 * strip + features + tenant portal sections. Everything below the
 * component (resources hub, testimonials, faq, footer) is untouched.
 */
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import DisputeResourcesSection from "@/components/landing/DisputeResourcesSection";

// Lifestyle imagery — Unsplash rights-cleared portraits used inside
// pastel blob backgrounds. Landlord portrait for the hero, tenant
// portrait for the "eligibility" card so both personas see themselves
// above the fold.
const HERO_PORTRAIT   = "/illustrations/landlord.svg";
const TENANT_PORTRAIT = "/illustrations/tenant.svg";

// Trust chips render as label-only pills — icons removed per user
// request (Feb 2026). Fourth chip surfaces the elastic-scale
// architecture in a phrase visitors can screenshot.
const TRUST_CHIPS = [
  "Compliant in all 13 provinces",
  "Direct-deposit rent",
  "Human PM support on call",
  "Scales to any portfolio — no rewrites needed",
];

const CHECKLIST = [
  "One dashboard for every property",
  "Compliant leases in every province",
  "Automated rent reminders and receipts",
  "Tax-ready reports (T776, NR4, T5013)",
  "Real-time maintenance triage",
];

// QUICK_START — kept as an array of {label, to} pairs. Colored icon
// tiles were removed per user request (Feb 2026, screenshot marked in
// pink). Only the wordings + arrow remain.
const QUICK_START = [
  { label: "List a property",       to: "/signup?role=landlord" },
  { label: "Screen a tenant",       to: "/signup?role=landlord" },
  { label: "Import your portfolio", to: "/get-started" },
  { label: "How it works",          to: "/features" },
];

const PROVINCES = [
  "Alberta (AB)", "British Columbia (BC)", "Manitoba (MB)",
  "New Brunswick (NB)", "Newfoundland & Labrador (NL)", "Northwest Territories (NT)",
  "Nova Scotia (NS)", "Nunavut (NU)", "Ontario (ON)",
  "Prince Edward Island (PE)", "Quebec (QC)", "Saskatchewan (SK)", "Yukon (YT)",
];

// Long pill CTA in Pocketpills style — dark rounded-full body with a
// circular emerald arrow puck on the right. Two variants: `dark`
// (full-width dark background) and `light` (white pill on a pastel
// card).
function PillCTA({ to, label, variant = "dark", testid }) {
  const base = "group inline-flex w-full items-center justify-between gap-4 rounded-full py-4 pl-7 pr-2 text-base sm:text-lg font-extrabold shadow-lg transition-transform hover:-translate-y-0.5";
  const skin = variant === "dark"
    ? "bg-slate-950 text-white shadow-slate-900/25"
    : "bg-white text-slate-950 shadow-slate-900/10";
  const puck = variant === "dark"
    ? "bg-emerald-500 text-white"
    : "bg-emerald-600 text-white";
  return (
    <Link to={to} data-testid={testid} className={`${base} ${skin}`}>
      <span>{label}</span>
      <span className={`w-11 h-11 rounded-full flex items-center justify-center transition-transform group-hover:translate-x-0.5 ${puck}`}>
        <ArrowRight className="w-5 h-5"/>
      </span>
    </Link>
  );
}

export default function PocketpillsHome() {
  return (
    <div className="bg-slate-50 pb-16" data-testid="pp-home">
      {/* HERO — big rounded pastel emerald card with portrait + dark pill CTA */}
      <section className="pt-6 sm:pt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-b from-emerald-100 via-emerald-50 to-white px-6 sm:px-12 pt-10 sm:pt-16 pb-8">
            {/* soft blob accents */}
            <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-emerald-200/60 blur-2xl"/>
            <div className="pointer-events-none absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-teal-200/50 blur-2xl"/>

            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 min-w-0 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-emerald-200 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-800">
                  <MapPin className="w-3.5 h-3.5"/> Built in Canada · Made for Canadian landlords
                </div>
                <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-slate-950">
                  Your proven <span className="text-emerald-600">rental partner.</span>
                  <br/>
                  Managed from one dashboard.
                </h1>
                <p className="mt-5 text-lg text-slate-700 leading-relaxed max-w-xl mx-auto lg:mx-0">
                  Central Rentals collects rent, screens tenants, files provincial notices, and hands your accountant tax-ready reports — all in Canadian dollars, PIPEDA-compliant.
                </p>
                <div className="mt-8 max-w-md mx-auto lg:mx-0 flex flex-wrap gap-3 justify-center lg:justify-start">
                  <PillCTA to="/signup" label="Start your subscription" variant="dark" testid="pp-hero-cta"/>
                  <Link
                    to="/pricing"
                    data-testid="pp-hero-see-pricing"
                    className="inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full border-2 border-slate-900 text-slate-900 font-bold text-sm hover:bg-slate-900 hover:text-white transition-colors"
                  >
                    See pricing →
                  </Link>
                </div>
                <div className="mt-3 text-xs font-semibold text-emerald-800" data-testid="pp-hero-price-teaser">
                  Plans from CA$5/mo · 14-day free trial · Cancel anytime
                </div>
                <div className="mt-4 text-xs text-slate-600 text-center lg:text-left">
                  Already have an account?{" "}
                  <Link to="/login/landlord" data-testid="pp-hero-login-ll"
                    className="font-bold text-slate-900 hover:text-emerald-700 underline-offset-2 hover:underline">Landlord login</Link>
                  <span className="mx-2 text-slate-400">·</span>
                  <Link to="/login/tenant" data-testid="pp-hero-login-tn"
                    className="font-bold text-slate-900 hover:text-emerald-700 underline-offset-2 hover:underline">Tenant login</Link>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="relative aspect-[4/5] rounded-[32px] overflow-hidden bg-emerald-200/60">
                  <img
                    src={HERO_PORTRAIT}
                    alt="Illustration of a Canadian landlord in front of a rental property holding keys"
                    className="absolute inset-0 w-full h-full object-contain p-4"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST CHIPS — pill row, wordings only (icons removed per user request Feb 2026). */}
      <section className="mt-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center sm:justify-start">
            {TRUST_CHIPS.map((label) => (
              <div key={label} data-testid="pp-trust-chip"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-100 px-3 sm:px-4 py-2 sm:py-2.5 shadow-sm">
                <span className="text-xs sm:text-sm font-semibold text-slate-800">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RENTAL READINESS AUDIT — modern glass card, no cartoon.
          The right-hand mock is a semantic, on-brand preview of the
          3-question audit itself so the CTA promise is visible before
          the click. */}
      <section className="mt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="pp-aurora-card relative overflow-hidden rounded-[32px] bg-emerald-100/70 p-6 sm:p-10 min-h-[420px] flex flex-col justify-between">
            <div className="pp-blob pp-blob-a pointer-events-none absolute rounded-full"/>
            <div className="pp-blob pp-blob-b pointer-events-none absolute rounded-full"/>
            <div className="pp-blob pp-blob-c pointer-events-none absolute rounded-full"/>
            <div className="pp-orbit pointer-events-none absolute top-4 left-10 w-40 h-40 rounded-full border border-emerald-300/60"/>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-8 items-center">
              {/* LEFT: copy + CTA */}
              <div className="max-w-md">
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-800">
                  90-second audit · No signup
                </div>
                <h2 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-slate-950 leading-[1.05]">
                  Check your rental readiness.
                </h2>
                <p className="mt-3 text-slate-700 text-base leading-relaxed">
                  Answer 3 questions and we&rsquo;ll show you exactly which parts
                  of your portfolio you can automate with Central Rentals today.
                </p>
                <div className="mt-6">
                  <PillCTA
                    to="/get-started"
                    label="Start the 3-question audit"
                    variant="dark"
                    testid="pp-readiness-cta"
                  />
                </div>
                <div className="mt-3 text-[11px] text-slate-600" data-testid="pp-readiness-note">
                  Anonymous · nothing sent to Stripe · takes ~90 seconds
                </div>
              </div>

              {/* RIGHT: modern audit preview card (replaces the cartoon).
                  A semantic list of the exact 3 questions, styled as a
                  compact SaaS dashboard tile. */}
              <div
                className="relative rounded-2xl overflow-hidden hidden sm:block"
                data-testid="pp-readiness-preview"
                style={{
                  background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
                  boxShadow: "0 30px 60px -20px rgba(15,23,42,0.25), 0 0 0 1px rgba(148,163,184,0.14)",
                }}
              >
                <div style={{ height: "4px", background: "linear-gradient(90deg,#10b981 0%,#059669 50%,#0ea5e9 100%)" }} />
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700">Rental readiness</div>
                  <span className="text-[11px] font-semibold text-slate-500">3 questions</span>
                </div>
                <ol className="px-6 py-5 space-y-4 text-sm">
                  {[
                    { n: 1, q: "How many rental doors do you manage today?",     hint: "1  ·  2–10  ·  11–50  ·  50+" },
                    { n: 2, q: "How do you currently collect rent?",             hint: "E-Transfer  ·  Cheque  ·  PAD  ·  Other" },
                    { n: 3, q: "Which province holds the majority of your units?", hint: "ON  ·  BC  ·  AB  ·  QC  ·  Other" },
                  ].map((row) => (
                    <li key={row.n} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex-none w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                        style={{ background: "rgba(16,185,129,0.14)", color: "#065f46" }}
                      >
                        {row.n}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 leading-snug">{row.q}</div>
                        <div className="mt-1 text-[11px] text-slate-500 tabular-nums">{row.hint}</div>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px]">
                  <span className="font-semibold text-slate-700">Your automation score</span>
                  <span className="font-bold tabular-nums" style={{ color: "#059669" }}>revealed in 90 s →</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SMALL-LANDLORD STATS TEASER — link into the research page for
          on-site engagement + it reinforces the "built for small
          landlords" positioning that drives our SEO cluster. */}
      <section className="mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Link to="/reports/canadian-landlord-statistics-2026" data-testid="home-stats-teaser"
            className="group block rounded-3xl bg-gradient-to-br from-slate-950 to-slate-900 text-white p-6 sm:p-10 hover:from-slate-900 hover:to-slate-800 transition-colors">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-300">Central Rentals · Research 2026</div>
            <div className="mt-3 grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 items-center">
              <div className="font-display text-6xl sm:text-7xl font-black text-emerald-400 leading-none">72%</div>
              <div>
                <div className="font-display text-xl sm:text-2xl font-extrabold leading-snug">
                  of Canadian rentals are owned by landlords with fewer than 5 units.
                </div>
                <div className="mt-2 text-slate-300 text-sm sm:text-base">
                  Small landlords run the country's rental market — and we built Central Rentals for them. See 14 more data points on the Canadian rental economy in our free 2026 report.
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 font-bold text-sm text-emerald-300 group-hover:gap-2 transition-all">
                  See the data <span>→</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FREE TOOLS STRIP — top-of-funnel backlink magnets. Zero login,
          fully client-side (invoice) + AI-polished (email). */}
      <section className="mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700">Free tools · No signup</div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-slate-950">Get to a professional invoice or email in 60 seconds</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <Link to="/tools/invoice-generator" data-testid="home-free-tool-invoice"
              className="group rounded-3xl bg-white border-2 border-emerald-100 hover:border-emerald-600 hover:shadow-lg p-6 sm:p-8 transition-all">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Free tool</div>
              <div className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-slate-950">Rental invoice generator</div>
              <p className="mt-2 text-slate-600 text-sm">Canada-tax-aware invoice (GST/HST/PST/QST by province) in your browser. Print or save as PDF — data never leaves the tab.</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 font-bold text-sm group-hover:gap-2 transition-all">
                Open the generator <span>→</span>
              </div>
            </Link>
            <Link to="/tools/cold-email-builder" data-testid="home-free-tool-email"
              className="group rounded-3xl bg-white border-2 border-emerald-100 hover:border-emerald-600 hover:shadow-lg p-6 sm:p-8 transition-all">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Free tool</div>
              <div className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-slate-950">Cold email builder</div>
              <p className="mt-2 text-slate-600 text-sm">20 landlord + tenant email templates (late rent, renewal, inspection, move-out, outreach) with one-click AI polish. No signup.</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 font-bold text-sm group-hover:gap-2 transition-all">
                Open the builder <span>→</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* VALUE PILL CARD — pricing lives inside the landlord portal */}
      <section className="mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-1 gap-4 sm:gap-6">
          <div data-testid="pp-value-card-tn"
            className="rounded-[52px] bg-lime-100/70 py-14 px-8 text-center">
            <div className="text-slate-950 font-semibold text-base sm:text-lg">Tenant portal</div>
            <div className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-emerald-700">
              Always free
            </div>
            <div className="mt-3 font-display text-2xl font-extrabold text-slate-950">
              $0 <span className="text-slate-500 font-semibold text-base">for renters</span>
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST CARD — long pill CTA */}
      <section className="mt-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-emerald-700 font-bold text-base">Landlord software</div>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold text-slate-950 leading-[1.03] tracking-tight">
            Easy to start — <span className="text-emerald-600">easier</span> to scale.
          </h2>
          <ul className="mt-8 space-y-5">
            {CHECKLIST.map((item) => (
              <li key={item} className="flex items-start gap-4">
                <span className="mt-0.5 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/>
                  </svg>
                </span>
                <span className="text-lg text-slate-800 leading-relaxed pt-0.5">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <PillCTA to="/signup" label="Start your subscription" variant="dark" testid="pp-checklist-cta"/>
          </div>
        </div>
      </section>

      {/* DARK ROUNDED CTA — big statement card with emerald blobs */}
      <section className="mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-[36px] bg-slate-950 text-white px-8 py-16 sm:p-16">
            <div className="pp-dark-blob pp-dark-blob-a pointer-events-none absolute"/>
            <div className="pp-dark-blob pp-dark-blob-b pointer-events-none absolute"/>
            <div className="pp-dark-blob pp-dark-blob-c pointer-events-none absolute"/>

            <div className="relative max-w-2xl">
              <div className="text-emerald-300 text-lg sm:text-xl font-semibold">Property management, simplified.</div>
              <h3 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold leading-[1.03] tracking-tight">
                Transform your rental business today.
              </h3>
              <p className="mt-5 text-slate-300 leading-relaxed">
                Join thousands of Canadian landlords who've swapped spreadsheets, USB drives, and shoeboxes of receipts for one clean dashboard.
              </p>
              <div className="mt-10 max-w-md">
                <PillCTA to="/signup" label="Start your subscription" variant="light" testid="pp-dark-cta"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GET STARTED LIST — icon tiles removed, wordings only. */}
      <section className="mt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="rounded-[32px] bg-emerald-50 p-6 sm:p-10">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">Get Started</div>
            <div className="mt-6 divide-y divide-emerald-100/70">
              {QUICK_START.map(({ label, to }) => (
                <Link key={label} to={to} data-testid={`pp-quick-${label.toLowerCase().replace(/\s+/g,'-')}`}
                  className="group flex items-center gap-4 py-4 hover:bg-white/60 transition-colors px-2 rounded-2xl">
                  <span className="text-lg sm:text-xl font-bold text-slate-900 flex-1">{label}</span>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all"/>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE — Central Rentals serves */}
      <section className="mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="rounded-[32px] bg-slate-50 border border-slate-200 p-6 sm:p-10 text-center">
            <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-950 leading-tight">
              Central Rentals serves:
            </h3>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-y-3 gap-x-6 text-slate-700 text-base sm:text-lg text-left max-w-3xl mx-auto">
              {PROVINCES.map((p) => (
                <div key={p} data-testid="pp-coverage-item">{p}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dispute resolution guide — landlord and tenant scenarios
          with concrete action steps. Ships as a home-page SEO block
          plus internal-link pass to tools & answer pages. */}
      <DisputeResourcesSection />
    </div>
  );
}
